import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";

import { FaqList } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { AGENCY } from "@/lib/agency";
import { buildHead, orgRef } from "@/lib/seo";
import { absoluteUrl } from "@/lib/site-config";
import { getServiceArea, serviceAreas, type ServiceArea } from "@/lib/service-areas";
import { isPlannedSuburbPage } from "@/lib/suburb-pages";
import { getSuburbProfile } from "@/lib/suburb-profiles";
import { ContentByline } from "@/components/ContentByline";
import { getLoanProduct, loanProducts, type LoanProduct } from "@/lib/loan-products";
import { getPillarContent, type PillarContent } from "@/lib/pillar-content";

/* ---------- Icon mapping ---------- */
const PRODUCT_ICON_NAME: Record<string, string> = {
  "sba-loans": "award",
  "sba-7a-loan": "badgecheck",
  "sba-loan-for-daycare": "heart",
  "sba-loan-for-franchise": "store",
  "sba-express-loan-for-veterans": "shieldcheck",
  "working-capital-loans": "wallet",
  "business-line-of-credit": "creditcard",
  "small-business-loans": "briefcase",
  "short-term-business-loans": "zap",
  "merchant-cash-advance": "receipt",
  "unsecured-business-loans": "sparkles",
  "startup-business-loans": "lightbulb",
  "business-acquisition-loans": "handcoins",
  "accounts-receivable-financing": "filetext",
  "invoice-factoring": "receipt",
  "revenue-based-financing": "trendingup",
  "same-day-business-loans": "clock",
  "business-lenders-near-me": "mappin",
  "business-loan-broker": "compass",
  "commercial-loan-broker": "building2",
  "equipment-financing": "wrench",
  "manufacturing-equipment-financing": "factory",
  "agriculture-equipment-financing": "tractor",
  "landscaping-equipment-financing": "scissors",
  "dental-practice-loans": "stethoscope",
  "medical-practice-loans": "stethoscope",
  "restaurant-loans": "utensils",
  "trucking-business-loans": "truck",
  "commercial-construction-loan": "hammer",
  "hotel-loans": "hotel",
  "salon-business-loans": "scissors",
  "retail-business-loans": "store",
  "auto-repair-shop-financing": "car",
  "ecommerce-business-funding": "shoppingbag",
  "food-truck-financing": "coffee",
  "loan-for-gym-business": "dumbbell",
  "farm-credit-financing": "sprout",
  "women-owned-business-funding": "users",
};
const iconFor = (slug: string) => PRODUCT_ICON_NAME[slug] ?? "dollarsign";

/* ---------- Imagery ---------- */
const u = (id: string, w = 1100) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=70`;
const HERO_POOL = [
  u("photo-1556761175-5973dc0f32e7"),
  u("photo-1521791136064-7986c2920216"),
  u("photo-1454165804606-c3d57bc86b40"),
  u("photo-1573497019940-1c28c88b4f3e"),
  u("photo-1556742502-ec7c0e9f34b1"),
  u("photo-1554224155-1696413565d3"),
  u("photo-1581094794329-c8112a89af12"),
  u("photo-1450101499163-c8848c66ca85"),
  u("photo-1556761175-b413da4baf72"),
  u("photo-1504307651254-35680f356dfd"),
  u("photo-1517248135467-4c7edcad34c4"),
  u("photo-1576091160550-2173dba999ef"),
];
function pickImage(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return HERO_POOL[h % HERO_POOL.length];
}

/* ---------- Helpers ---------- */
function cap(s: string) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
function termLengthFor(product: LoanProduct): string {
  const slug = product.slug.toLowerCase();
  if (slug.includes("line-of-credit")) return "Revolving · 6-24 mo.";
  if (slug.includes("sba")) return "5-25 years";
  if (slug.includes("equipment")) return "2-7 years";
  if (slug.includes("short-term")) return "3-18 months";
  if (slug.includes("merchant") || slug.includes("same-day")) return "3-18 months";
  if (slug.includes("factoring") || slug.includes("receivable"))
    return "Per invoice · 30-90 days";
  if (slug.includes("acquisition") || slug.includes("construction") || slug.includes("hotel"))
    return "5-25 years";
  return "6 months - 10 years";
}

/* ---------- Route ---------- */
export const Route = createFileRoute("/dallas/$slug/$product")({
  loader: ({ params }) => {
    // Only the 80 planned suburb pages exist (Excel Dallas tab); every other
    // {suburb}/{product} combo 404s instead of becoming a doorway page.
    if (!isPlannedSuburbPage(params.slug, params.product)) throw notFound();
    const area = getServiceArea(params.slug);
    const product = getLoanProduct(params.product);
    if (!area || !product) throw notFound();
    const content = getPillarContent(product.slug);
    return { area, product, content };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { area, product } = loaderData;
    const url = `/dallas/${params.slug}/${params.product}`;
    const title = `${product.name} in ${area.city}, ${area.state} | ${AGENCY.name}`;
    const description =
      `${product.name} for ${area.city}, ${area.state} businesses. ${product.tagline} ` +
      `Typical ${product.range}, ${product.speed}. Arranged by ${AGENCY.name} - no upfront fees.`.slice(
        0,
        158,
      );
    const img = pickImage(`${area.slug}-${product.slug}`);
    const head = buildHead({
      title,
      description,
      path: url,
      image: img,
      type: "article",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Dallas", path: "/dallas/" },
        { name: `${area.city}, ${area.state}`, path: `/service-areas/${area.slug}` },
        { name: product.name, path: url },
      ],
      graph: [
        {
          "@type": "FinancialProduct",
          name: `${product.name} in ${area.city}, ${area.state}`,
          description: product.description,
          provider: orgRef,
          areaServed: { "@type": "City", name: `${area.city}, ${area.state}` },
          url: absoluteUrl(url),
        },
      ],
    });
    // Preload the eager above-the-fold hero so the browser fetches the LCP
    // image immediately instead of after HTML parse/hydration.
    return {
      ...head,
      links: [
        ...head.links,
        { rel: "preload", as: "image", href: img, fetchPriority: "high" },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl font-semibold">Page not found</h1>
      <p className="mt-4 text-muted-foreground">
        We couldn't find that area and product combination.{" "}
        <Link to="/service-areas" className="text-accent underline">
          See all areas
        </Link>
        .
      </p>
    </div>
  ),
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl font-semibold">Something went wrong</h1>
      <p className="mt-4 text-muted-foreground">{error.message}</p>
      <Button onClick={reset} className="mt-6">
        Try again
      </Button>
    </div>
  ),
  component: AreaProductPage,
});

/* ---------- Local context (unique per suburb) ---------- */
function LocalContext({ area, product }: { area: ServiceArea; product: LoanProduct }) {
  const profile = getSuburbProfile(area.slug);
  if (!profile) return null;
  return (
    <section className="border-b border-border bg-secondary">
      <div className="mx-auto max-w-5xl px-6 py-14 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            In {profile.city}, {area.state}
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            How {profile.city} businesses use {product.name}
          </h2>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
            Financing isn't one-size-fits-all in {profile.city}. {product.name} gets
            structured around the businesses that actually operate here, so it helps to
            start with the local economy.
          </p>
          <div className="mt-5 max-w-3xl space-y-4 text-base leading-relaxed text-muted-foreground">
            {profile.economy.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          <div className="mt-9 grid gap-8 sm:grid-cols-2">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/70">
                Key local industries
              </p>
              <ul className="mt-3 flex flex-wrap gap-2">
                {profile.industries.map((ind) => (
                  <li
                    key={ind}
                    className="rounded-full border border-border bg-background px-3 py-1 text-xs text-foreground/80"
                  >
                    {ind}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/70">
                Business districts we serve
              </p>
              <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                {profile.districts.map((d) => (
                  <li key={d} className="flex items-start gap-2">
                    <Icon name="mappin" color="co" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {profile.scenarios.map((s) => (
              <div key={s.h} className="rounded-2xl border border-border bg-background p-5">
                <h3 className="font-serif text-lg font-semibold leading-snug">{s.h}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.p}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {profile.proximityNote}
          </p>
          {profile.nearby.length > 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              {product.name} in nearby areas:{" "}
              {profile.nearby.map((slug, i) => {
                const np = getSuburbProfile(slug);
                if (!np) return null;
                return (
                  <span key={slug}>
                    {i > 0 ? ", " : ""}
                    <Link
                      to="/dallas/$slug/$product"
                      params={{ slug, product: product.slug }}
                      className="font-medium text-accent hover:underline"
                    >
                      {np.city}
                    </Link>
                  </span>
                );
              })}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */
function AreaProductPage() {
  const { area, product, content } = Route.useLoaderData();
  const heroImg = pickImage(`${area.slug}-${product.slug}`);
  const related = loanProducts.filter((p) => p.slug !== product.slug).slice(0, 6);

  return (
    <>
      <article>
        <Hero area={area} product={product} content={content} heroImg={heroImg} />
        <div className="border-b border-border bg-background">
          <div className="mx-auto max-w-3xl px-6 py-4 lg:px-10">
            <ContentByline />
          </div>
        </div>
        <WhatItIs area={area} product={product} content={content} />
        <BestFor area={area} product={product} content={content} />
        <LocalContext area={area} product={product} />
        <TypicalTerms area={area} product={product} content={content} />
        <HowItWorks area={area} product={product} />
        <Benefits area={area} product={product} />
        <CommonUses area={area} product={product} content={content} />
        <Faq area={area} product={product} content={content} />
        <Related area={area} related={related} />
        <FinalCta area={area} product={product} />
      </article>
      <MobileStickyBar applyHref="/apply-funding" />
    </>
  );
}

/* ---------- Hero ---------- */
function Hero({
  area,
  product,
  content,
  heroImg,
}: {
  area: ServiceArea;
  product: LoanProduct;
  content?: PillarContent;
  heroImg: string;
}) {
  const benefits = [
    "No upfront fees, ever",
    "Compare multiple lenders in one call",
    "Texas-based advisors",
    "Soft credit pull (no credit hit to quote)",
  ];
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 85% 0%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, oklch(0.3 0.1 260) 0%, transparent 50%)",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-6 py-14 md:py-20 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10 lg:px-10">
        <Reveal>
          <nav
            aria-label="Breadcrumb"
            className="flex flex-wrap items-center gap-1.5 text-xs text-primary-foreground/70"
          >
            <Link to="/" className="hover:text-accent">Home</Link>
            <Icon name="chevronright" color="bl" className="h-3 w-3 brightness-0 invert" />
            <Link to="/dallas" className="hover:text-accent">Business Loans</Link>
            <Icon name="chevronright" color="bl" className="h-3 w-3 brightness-0 invert" />
            <Link
              to="/dallas/$slug"
              params={{ slug: area.slug }}
              className="hover:text-accent"
            >
              {area.city}
            </Link>
            <Icon name="chevronright" color="bl" className="h-3 w-3 brightness-0 invert" />
            <span className="text-primary-foreground/85">{product.name}</span>
          </nav>

          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/85">
            <Icon name="mappin" color="bl" className="h-3 w-3 text-accent brightness-0 invert" />{" "}
            Serving {area.city}, {area.state}
          </p>
          <h1 className="mt-4 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
            {product.name} in {area.city}, {area.state}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-primary-foreground/85">
            {content?.whatItIs?.[0] ?? product.description} Arranged by{" "}
            {AGENCY.name} for owners in {area.city} and across the DFW metro.
            One advisor, real term sheets, no upfront fees.
          </p>

          <ul className="mt-6 grid max-w-xl gap-2 text-sm text-primary-foreground/90 sm:grid-cols-2">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <Icon
                  name="checkcircle2"
                  color="bl"
                  className="mt-0.5 h-4 w-4 shrink-0 text-accent brightness-0 invert"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-7 flex flex-wrap gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert" /> Call{" "}
                {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </div>
        </Reveal>

        {/* Key-facts card + image */}
        <Reveal delay={120} className="relative">
          <div className="relative overflow-hidden rounded-3xl border border-primary-foreground/15 bg-primary-foreground/5 shadow-2xl backdrop-blur">
            <div className="relative h-44 w-full overflow-hidden sm:h-56">
              <img
                src={heroImg}
                alt={`${product.name} for ${area.city} business owners`}
                loading="eager"
                decoding="async"
                className="h-full w-full object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent"
              />
            </div>
            <div className="p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
                Program at a glance
              </p>
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4">
                <Fact
                  icon="wallet"
                  label="Typical loan amount"
                  value={content?.terms?.amount ?? product.range}
                />
                <Fact
                  icon="clock"
                  label="Funding speed"
                  value={content?.terms?.speed ?? product.speed}
                />
                <Fact
                  icon="clock"
                  label="Term length"
                  value={content?.terms?.term ?? termLengthFor(product)}
                />
                <Fact
                  icon="filetext"
                  label="To qualify"
                  value={
                    content?.terms?.qualifications ??
                    "6+ mo. operating · bank statements"
                  }
                />
              </dl>
              <p className="mt-5 text-[11px] leading-relaxed text-primary-foreground/65">
                Ranges are typical for {area.city}-area files. Final rates and
                terms depend on the lender and borrower profile.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Fact({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-foreground/65">
        <Icon name={icon} color="co" className="h-3.5 w-3.5" /> {label}
      </dt>
      <dd className="mt-1 font-serif text-lg leading-snug text-primary-foreground">
        {value}
      </dd>
    </div>
  );
}

/* ---------- What it is ---------- */
function WhatItIs({
  area,
  product,
  content,
}: {
  area: ServiceArea;
  product: LoanProduct;
  content?: PillarContent;
}) {
  const paragraphs =
    content?.whatItIs && content.whatItIs.length
      ? content.whatItIs
      : [
          product.description,
          `In practice, an owner in ${area.city} applies once with a short application and a few months of business bank statements. We compare offers from lenders actively writing ${product.name.toLowerCase()} for Texas borrowers, walk you through the real cost of capital (not just the headline rate), and you choose the offer that fits.`,
        ];
  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-3xl px-6 py-14 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            What it is
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            {product.name} in {area.city}, explained in plain English
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <p>
              {AGENCY.name} is not a lender. We're a Dallas-based advisory
              firm serving {area.city} that places your file with the lender
              most likely to fund it on the best terms, and we get paid only
              when your deal closes.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Who it's best for ---------- */
function BestFor({
  area,
  product,
  content,
}: {
  area: ServiceArea;
  product: LoanProduct;
  content?: PillarContent;
}) {
  const items =
    content?.bestForBusinesses && content.bestForBusinesses.length
      ? content.bestForBusinesses
      : product.bestFor;
  const situations =
    content?.situations && content.situations.length
      ? content.situations
      : situationsFor(product);

  return (
    <section className="border-b border-border bg-secondary/40">
      <div className="mx-auto max-w-6xl px-6 py-14 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Who it's best for
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            A good fit if you recognize yourself here
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            {product.name} tends to be the right choice for {area.city}{" "}
            operators in specific situations. If one or more of these
            describes you, it's worth a 15-minute call.
          </p>
        </Reveal>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <Reveal>
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-foreground">
              Business types
            </h3>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {items.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 rounded-xl border border-border bg-background p-4"
                >
                  <Icon name="building2" color="co" className="mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100}>
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-foreground">
              Situations it fits
            </h3>
            <ul className="mt-4 grid gap-3">
              {situations.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-3 rounded-xl border border-border bg-background p-4"
                >
                  <Icon name="checkcircle2" color="co" className="mt-0.5 h-5 w-5 shrink-0" />
                  <span className="text-sm">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function situationsFor(product: LoanProduct): string[] {
  const slug = product.slug.toLowerCase();
  const base = [
    "You need capital faster than your bank can move",
    "You've been declined or quoted poor terms elsewhere",
    "You want a transparent breakdown of total cost, not just APR",
  ];
  if (slug.includes("line-of-credit"))
    base.push("Cash flow swings month to month and you want a reusable cushion");
  if (slug.includes("equipment"))
    base.push("You're buying a specific asset and want it as collateral");
  if (slug.includes("sba"))
    base.push("You qualify for SBA terms and can wait 30-60 days to fund");
  if (slug.includes("merchant") || slug.includes("same-day"))
    base.push("You have an urgent expense and steady card or deposit volume");
  if (slug.includes("factoring") || slug.includes("receivable"))
    base.push("You invoice B2B customers on 30-90 day terms");
  if (slug.includes("startup"))
    base.push("You're under two years old and traditional lenders won't quote");
  if (slug.includes("acquisition"))
    base.push("You're buying an existing business or buying out a partner");
  return base.slice(0, 5);
}

/* ---------- Typical terms ---------- */
function TypicalTerms({
  area,
  product,
  content,
}: {
  area: ServiceArea;
  product: LoanProduct;
  content?: PillarContent;
}) {
  const t = content?.terms;
  const rows = [
    { label: "Typical loan amount", value: t?.amount ?? product.range },
    { label: "Term length", value: t?.term ?? termLengthFor(product) },
    { label: "Time to funding", value: t?.speed ?? product.speed },
    {
      label: "Qualification basics",
      value:
        t?.qualifications ??
        "Business 6+ months operating, 3 months of business bank statements, U.S. business entity",
    },
    {
      label: "Documentation",
      value:
        t?.documentation ??
        "One-page application; tax returns generally not required under $250K",
    },
    { label: "Cost to apply", value: "$0 upfront. No fees, no obligation." },
  ];
  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-5xl px-6 py-14 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Typical terms
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            The numbers, as straight as we can give them
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            These are typical ranges for {product.name.toLowerCase()} files we
            place for {area.city}-area borrowers. Final rates and terms depend
            on the lender and the borrower profile.
          </p>
        </Reveal>
        <Reveal delay={80}>
          <dl className="mt-8 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-background">
            {rows.map((r) => (
              <div
                key={r.label}
                className="grid gap-1 px-5 py-4 sm:grid-cols-[220px_1fr] sm:items-center sm:gap-6"
              >
                <dt className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {r.label}
                </dt>
                <dd className="font-serif text-base text-foreground">{r.value}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- How to apply ---------- */
function HowItWorks({ area, product }: { area: ServiceArea; product: LoanProduct }) {
  const steps = [
    {
      n: "01",
      h: `Talk to a ${area.city} advisor`,
      p: `Quick 15-minute call. We confirm ${product.name.toLowerCase()} is the right structure for your ${area.city} business, or recommend a better-fitting program.`,
    },
    {
      n: "02",
      h: "Submit your file",
      p: "One-page application plus 3 months of business bank statements. Most files don't require tax returns.",
    },
    {
      n: "03",
      h: "Compare real offers",
      p: "Within 24-72 hours we bring back 2-4 funded offers from lenders actively writing this program for Texas borrowers.",
    },
    {
      n: "04",
      h: "Sign and fund",
      p: "Pick the offer that fits. Funds land in your business account, often the same business day.",
    },
  ];
  return (
    <section
      id="apply"
      className="relative overflow-hidden border-b border-border bg-secondary/40 scroll-mt-20"
    >
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            How to apply
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            From first call to funded in days
          </h2>
        </Reveal>
        <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Reveal key={s.n} as="li" delay={i * 80}>
              <div className="relative h-full rounded-2xl border border-border bg-background p-6">
                <span className="font-serif text-4xl font-semibold text-accent">
                  {s.n}
                </span>
                <h3 className="mt-3 text-base font-semibold">{s.h}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
              </div>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={320}>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full">
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert" />{" "}
                Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full"
            >
              <Link to="/apply-funding">Start my application</Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Benefits ---------- */
function Benefits({ area, product }: { area: ServiceArea; product: LoanProduct }) {
  const benefits = [
    {
      icon: "zap",
      h: "Decisions in hours",
      p: `Files for ${product.name.toLowerCase()} are usually screened the same day they arrive.`,
    },
    {
      icon: "handcoins",
      h: "No upfront fees",
      p: "We're paid by the lender on close. You see real offers before any commitment.",
    },
    {
      icon: "layers",
      h: "Multiple lenders, one file",
      p: "One application, side-by-side offers, so you choose on facts, not pressure.",
    },
    {
      icon: "shieldcheck",
      h: "Texas-based advisors",
      p: `Local underwriters who understand ${area.city} business profiles and Texas filings.`,
    },
    {
      icon: "filecheck",
      h: "Soft credit pull to quote",
      p: "Initial review is a soft pull, with no impact to your personal credit score.",
    },
    {
      icon: "trendingup",
      h: "Built around your cash cycle",
      p: "Term, draw structure, and payment cadence sized to how your revenue actually arrives.",
    },
  ];
  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Benefits
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            Why {area.city} owners pick {product.name.toLowerCase()} through us
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((b, i) => (
            <Reveal key={b.h} delay={i * 60}>
              <div className="group h-full rounded-2xl border border-border bg-background p-6 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-lg">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent">
                  <Icon name={b.icon} color="co" className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-serif text-lg font-semibold">{b.h}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {b.p}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Common uses ---------- */
function CommonUses({
  area,
  product,
  content,
}: {
  area: ServiceArea;
  product: LoanProduct;
  content?: PillarContent;
}) {
  const uses =
    content?.commonUses && content.commonUses.length
      ? content.commonUses
      : commonUsesFor(product);
  return (
    <section className="border-b border-border bg-secondary/40">
      <div className="mx-auto max-w-6xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Common uses
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            How {area.city} businesses actually use it
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            A few of the real-world reasons {product.name.toLowerCase()} shows
            up in our {area.city}-area pipeline each month.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {uses.map((u, i) => (
            <Reveal key={u.h} delay={i * 60}>
              <div className="h-full rounded-2xl border border-border bg-background p-6">
                <div className="flex items-center gap-2 text-accent">
                  <Icon name="sparkles" color="co" className="h-5 w-5" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                    Use case
                  </span>
                </div>
                <h3 className="mt-3 font-serif text-lg font-semibold">{u.h}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {u.p}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function commonUsesFor(product: LoanProduct): { h: string; p: string }[] {
  const slug = product.slug.toLowerCase();
  const generic = [
    {
      h: "Cover a payroll gap",
      p: "Bridge a slow receivable cycle without missing a pay period or shorting a key vendor.",
    },
    {
      h: "Restock inventory before a busy season",
      p: "Buy ahead at better unit pricing and have shelves full when local demand spikes.",
    },
    {
      h: "Take on a larger contract",
      p: "Fund the upfront materials and labor required to accept a bid you'd otherwise pass on.",
    },
    {
      h: "Refinance higher-cost debt",
      p: "Consolidate stacked MCAs or short-term advances into a cleaner, longer-term structure.",
    },
    {
      h: "Open a second location",
      p: "Cover build-out, deposits, and ramp-up payroll until the new site reaches steady revenue.",
    },
    {
      h: "Buy or upgrade equipment",
      p: "Acquire the machinery, vehicles, or tech you need without draining operating cash.",
    },
  ];
  if (slug.includes("line-of-credit"))
    return [
      {
        h: "Float seasonal swings",
        p: "Draw during slow months, repay during peaks. You only pay interest on what you use.",
      },
      {
        h: "Cover surprise repairs",
        p: "Replace a failed HVAC, compressor, or vehicle without an emergency-rate loan.",
      },
      {
        h: "Bid on bigger projects",
        p: "Show working capital reserves on RFPs without tying up your cash.",
      },
    ];
  if (slug.includes("equipment"))
    return [
      {
        h: "Buy a work truck or van",
        p: "Get the asset on the road this week; finance over its useful life.",
      },
      {
        h: "Upgrade to better margins",
        p: "Trade an aging unit for newer equipment that lowers fuel, labor, or maintenance cost.",
      },
      {
        h: "Add capacity for a new contract",
        p: "Bring on the second lift, oven, machine, or rig before turning down work.",
      },
    ];
  return generic.slice(0, 6);
}

/* ---------- FAQ ---------- */
function Faq({
  area,
  product,
  content,
}: {
  area: ServiceArea;
  product: LoanProduct;
  content?: PillarContent;
}) {
  const localized = [
    {
      q: `Do I need to be located in ${area.city} to apply?`,
      a: `Your business should be operating in or serving the ${area.city} area. Underwriting is driven by revenue, time in business, credit, and industry, not your specific ZIP code. We work with owners across the DFW metro.`,
    },
    {
      q: `How fast can a ${area.city} business get funded?`,
      a: `For ${product.name}, ${product.speed.toLowerCase()} is typical once the file is complete. We'll give you a realistic timeline on the first call based on the documents you have ready.`,
    },
    {
      q: `Do you have a ${area.city} office?`,
      a: `Our office is in Dallas at ${AGENCY.address}. We serve ${area.city} remotely by phone and secure document upload. We don't claim a branch where we don't have one.`,
    },
  ];

  const pillarFaqs =
    content?.faq && content.faq.length ? content.faq.slice(0, 4) : [];

  const fallback = pillarFaqs.length
    ? []
    : [
        {
          q: `What amounts are typical for ${product.name} in ${area.city}?`,
          a: `Typical amounts fall in the ${product.range} range. Final amounts depend on revenue, credit, and the lender's program. We don't promise a number until we've reviewed the file.`,
        },
        {
          q: "Will applying hurt my credit?",
          a: "The initial review uses a soft pull that doesn't affect your score. A hard pull only happens when you authorize moving forward with a specific lender on a specific offer.",
        },
        {
          q: "Do you charge any upfront fees?",
          a: `No. ${AGENCY.name} is paid by the lender on close. You see real offers before any commitment.`,
        },
      ];

  const entries = [...localized, ...pillarFaqs, ...fallback].slice(0, 7);

  return (
    <section className="border-b border-border bg-background">
      <div className="mx-auto max-w-3xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            FAQ
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            {product.name} in {area.city}: questions, answered
          </h2>
        </Reveal>
        <Reveal delay={80}>
          <FaqList
            faqs={entries.map((f) => ({
              q: /\?$/.test(f.q) ? cap(f.q) : `${cap(f.q)}?`,
              a: f.a,
            }))}
          />
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Related ---------- */
function Related({
  area,
  related,
}: {
  area: ServiceArea;
  related: LoanProduct[];
}) {
  const nearby = serviceAreas.filter((a) => a.slug !== area.slug).slice(0, 6);
  return (
    <section className="border-b border-border bg-secondary/40">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Related financing
          </p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            Other programs {area.city} owners look at
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            Not sure this is the right fit? These are the most common
            alternatives we place for {area.city} businesses.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((r, i) => (
            <Reveal key={r.slug} delay={i * 50}>
              <Link
                to="/dallas/$slug/$product"
                params={{ slug: area.slug, product: r.slug }}
                className="group flex h-full flex-col rounded-2xl border border-border bg-background p-5 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-lg"
              >
                <div className="flex items-center gap-2 text-accent">
                  <Icon name={iconFor(r.slug)} color="co" className="h-8 w-8" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em]">
                    Program
                  </span>
                </div>
                <h3 className="mt-3 font-serif text-lg font-semibold">{r.name}</h3>
                {r.tagline ? (
                  <p className="mt-2 flex-1 text-sm text-muted-foreground">
                    {r.tagline}
                  </p>
                ) : null}
                <p className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-accent">
                  View program{" "}
                  <Icon
                    name="chevronright"
                    color="bl"
                    className="h-3 w-3 transition group-hover:translate-x-0.5"
                  />
                </p>
              </Link>
            </Reveal>
          ))}
        </div>

        {nearby.length ? (
          <div className="mt-12">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Nearby service areas
            </p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {nearby.map((a) => (
                <li key={a.slug}>
                  <Link
                    to="/dallas/$slug"
                    params={{ slug: a.slug }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-sm transition hover:border-accent"
                  >
                    <Icon name="mappin" color="co" className="h-3.5 w-3.5" />
                    {a.city}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}

/* ---------- Final CTA ---------- */
function FinalCta({ area, product }: { area: ServiceArea; product: LoanProduct }) {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 100%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 90% 0%, oklch(0.3 0.1 260) 0%, transparent 50%)",
        }}
      />
      <div className="relative mx-auto max-w-4xl px-6 py-20 text-center lg:px-10">
        <Reveal>
          <p className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-foreground/85">
            <Icon name="users" color="co" className="h-4 w-4" /> Talk to a{" "}
            {area.city} advisor
          </p>
          <h2 className="mt-5 font-serif text-3xl font-semibold leading-tight md:text-5xl">
            Ready to talk {product.name.toLowerCase()} in {area.city}?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-primary-foreground/80">
            One 15-minute call tells you whether we can place your file, what
            terms to expect, and what your application needs to look like
            first.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert" />{" "}
                Call {AGENCY.phone}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
          </div>
          <p className="mt-6 text-xs text-primary-foreground/60">
            {AGENCY.hours} · {AGENCY.address}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
